import { useEffect, useRef } from "react";
import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { Spinner } from "@/components/ui/Spinner";
import { useProposeClaimSet } from "@/hooks/use-claim-strategy";
import { plural } from "@/lib/plural";
import { refusalDetail } from "@/services/api-client";

const NOTHING_LANDED = "No claim was proposed from the positions you agreed.";
const EMPTIED =
  "Every claim was removed. A new proposal fills the claim forms you kept.";
const STRANDED = "The proposal is taking longer than it should.";
const UNREACHED = "The claim set could not be asked for.";

/** Whether the stage is the empty one a first proposal is asked for on entry. */
export function asksOnEntry(stage: ClaimStrategyResponse): boolean {
  return (
    stage.gate.positions_settled &&
    stage.claims.length === 0 &&
    stage.proposal.status === "not_started"
  );
}

/**
 * Whether a first proposal is on its way into an empty stage, when the set
 * has nothing to draw yet and every agreed position would read as unplaced.
 */
export function awaitingFirstProposal(stage: ClaimStrategyResponse): boolean {
  const { proposal, claims } = stage;
  return (
    claims.length === 0 &&
    (asksOnEntry(stage) || (proposal.status === "running" && !proposal.stale))
  );
}

const NOTHING_KEPT = "No claim was kept from the proposal.";

/** How many proposed claims were set aside and why, or nothing when none was. */
function setAside(skipped: number): string | null {
  if (skipped === 0) return null;
  const aside = plural(skipped, "proposed claim");
  return `${aside} ${skipped === 1 ? "was" : "were"} set aside: they did not hold together, or there was no room for their form.`;
}

/** Why the attorney may ask again, in words, or nothing while there is no reason to. */
function askAgainBecause(stage: ClaimStrategyResponse): string | null {
  const { proposal, claims, gate } = stage;
  if (!gate.positions_settled || claims.length > 0) return null;
  if (proposal.status === "failed") return proposal.error ?? UNREACHED;
  if (proposal.status === "complete") {
    if (proposal.placed > 0) return EMPTIED;
    // A model that proposed nothing and one whose every claim was dropped read differently.
    const aside = setAside(proposal.skipped);
    return aside === null ? NOTHING_LANDED : `${NOTHING_KEPT} ${aside}`;
  }
  if (proposal.status === "running" && proposal.stale) return STRANDED;
  return null;
}

/** What the landed proposal came to, under the head. */
function placedNote(stage: ClaimStrategyResponse): string | null {
  const { proposal, claims } = stage;
  if (proposal.status !== "complete" || claims.length === 0) return null;
  const placed = `The assistant placed ${String(proposal.placed)} of your positions into this set.`;
  const aside = setAside(proposal.skipped);
  return aside === null ? placed : `${placed} ${aside}`;
}

/**
 * The first proposal: asked for once on entry into an empty stage, busy until
 * the run settles, and asked for again only when the attorney says so.
 *
 * The ask on entry is latched per task in a ref, so React StrictMode's replayed
 * mount effect asks once; the accepted run is patched into the cache before a
 * second render could find a run to ask for.
 */
export function ProposalState({
  taskId,
  stage,
}: {
  taskId: string;
  stage: ClaimStrategyResponse;
}) {
  const propose = useProposeClaimSet(taskId);
  const askedRef = useRef<string | null>(null);
  const entry = asksOnEntry(stage);
  const { mutate } = propose;
  useEffect(() => {
    if (!entry || askedRef.current === taskId) return;
    askedRef.current = taskId;
    mutate();
  }, [entry, taskId, mutate]);

  // An empty stage reads busy from the first render until the ask is answered; a
  // refused ask ends that, or the page would wait on a run that was never started.
  const busy =
    propose.isPending ||
    (entry && !propose.isError) ||
    (stage.proposal.status === "running" && !stage.proposal.stale);
  if (busy) {
    return (
      <Panel role="status" className="mt-6 flex items-center gap-3 text-sm">
        <Spinner />
        <span>
          <strong>Proposing a claim set…</strong> The assistant is placing the
          positions you agreed into a claim set, organised by form.
        </span>
      </Panel>
    );
  }
  const refusedOnEntry = entry && propose.isError;
  const reason = refusedOnEntry
    ? refusalDetail(propose.error, UNREACHED)
    : askAgainBecause(stage);
  const note = placedNote(stage);
  return (
    <>
      {propose.isError && !refusedOnEntry && (
        <Panel as="p" role="alert" tone="destructive" className="mt-6 text-sm">
          {refusalDetail(propose.error, UNREACHED)}
        </Panel>
      )}
      {reason !== null && (
        <Panel
          role="status"
          tone="warning"
          className="mt-6 flex flex-wrap items-center gap-3 text-sm"
        >
          <span className="flex-1">{reason}</span>
          <Button
            type="button"
            variant="secondary"
            size="dense"
            onClick={() => {
              mutate();
            }}
          >
            Ask again
          </Button>
        </Panel>
      )}
      {note !== null && (
        <p className="mt-4 text-sm text-muted-foreground">{note}</p>
      )}
    </>
  );
}
